import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/client';
import './FertRawMaterialPopup.css';

/**
 * Generic single-name list popup.
 * Props:
 *   addEndpoint   — POST URL,   e.g. '/api/fert-raw-materials-batch'
 *   editEndpoint  — PUT URL prefix, e.g. '/api/fert-raw-materials-batch' → PUT /:id
 *   idField       — PK field on editData,   default 'id'
 *   nameField     — name field on editData, default 'materialName'
 *   addTitle      — popup title for add mode
 *   editTitle     — popup title prefix for edit mode (appended with " #id")
 *   addSubtitle   — subtitle in add mode
 *   icon          — FA icon class, default 'fas fa-flask'
 *   placeholder   — input placeholder
 */
const FertRawMaterialPopup = ({
    isOpen, onClose, onSaved, editData = null,
    addEndpoint  = '/api/fert-raw-materials-batch',
    editEndpoint = '/api/fert-raw-materials-batch',
    idField      = 'id',
    nameField    = 'materialName',
    addTitle     = 'Add Fertilizer Types',
    editTitle    = 'Edit Fertilizer Type',
    addSubtitle  = 'Enter one or more names — press Enter to add a row',
    icon         = 'fas fa-flask',
    placeholder  = 'e.g. Mycorrhizal Fungi Granules',
}) => {
    const [rows,   setRows]   = useState(['']);
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    const firstInputRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        setRows(editData ? [editData[nameField] || ''] : ['']);
        setError('');
        setTimeout(() => firstInputRef.current?.focus(), 60);
    }, [isOpen, editData, nameField]);

    if (!isOpen) return null;

    const updateRow = (i, val) => setRows(prev => prev.map((r, idx) => idx === i ? val : r));
    const addRow    = () => setRows(prev => [...prev, '']);
    const removeRow = (i) => setRows(prev => prev.filter((_, idx) => idx !== i));

    const handleKeyDown = (e, i) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (editData) { handleSave(); return; }
        if (i === rows.length - 1) {
            setRows(prev => [...prev, '']);
            setTimeout(() => {
                const inputs = document.querySelectorAll('.frm-input');
                inputs[inputs.length - 1]?.focus();
            }, 30);
        }
    };

    const handleSave = async () => {
        setError('');
        const valid = rows.map(r => r.trim()).filter(Boolean);
        if (!valid.length) { setError('Please enter at least one name'); return; }
        setSaving(true);
        try {
            if (editData) {
                await api.put(`${editEndpoint}/${editData[idField]}`,
                    { [nameField]: valid[0] });
            } else {
                await api.post(addEndpoint, { names: valid });
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="frm-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="frm-popup">

                <div className="frm-header">
                    <div>
                        <div className="frm-title">
                            {editData ? editTitle : addTitle}
                        </div>
                        <div className="frm-subtitle">
                            {editData
                                ? `ID #${editData[idField]}`
                                : addSubtitle}
                        </div>
                    </div>
                    <button className="frm-close" onClick={onClose} aria-label="Close">
                        <i className="fas fa-times" />
                    </button>
                </div>

                <div className="frm-body">
                    <div className="frm-rows">
                        {rows.map((val, i) => (
                            <div className="frm-row" key={i}>
                                <div className="frm-row__num">
                                    {editData
                                        ? <i className={`${icon} frm-icon`} />
                                        : <span className="frm-row__index">{i + 1}</span>
                                    }
                                </div>
                                <input
                                    ref={i === 0 ? firstInputRef : null}
                                    className="frm-input"
                                    value={val}
                                    onChange={e => updateRow(i, e.target.value)}
                                    onKeyDown={e => handleKeyDown(e, i)}
                                    placeholder={placeholder}
                                    autoComplete="off"
                                />
                                {!editData && rows.length > 1 && (
                                    <button className="frm-remove" onClick={() => removeRow(i)} title="Remove">
                                        <i className="fas fa-times" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>

                    {!editData && (
                        <button className="frm-add-row" onClick={addRow}>
                            <i className="fas fa-plus" />
                            Add another
                        </button>
                    )}

                    {error && <div className="frm-error"><i className="fas fa-exclamation-triangle" /> {error}</div>}
                </div>

                <div className="frm-footer">
                    <button className="frm-btn frm-btn--ghost" onClick={onClose}>Cancel</button>
                    <button className="frm-btn frm-btn--primary" onClick={handleSave} disabled={saving}>
                        {saving
                            ? <><i className="fas fa-spinner fa-spin" /> Saving…</>
                            : editData
                                ? <><i className="fas fa-check" /> Save</>
                                : <><i className="fas fa-plus" /> Add {rows.filter(r => r.trim()).length > 1 ? `${rows.filter(r => r.trim()).length} items` : 'item'}</>
                        }
                    </button>
                </div>

            </div>
        </div>
    );
};

export default FertRawMaterialPopup;
